/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
// @ts-nocheck
import axios from 'axios';
import { SwalToast } from '../components/Common';
import utils from './utils';

const UNAUTHORIZED_RESPONSE_CODE = 401;

const logout = () => {
  const { activeProject, userId, userAndIdDetails } =
    utils.getUserDetailsFromStorage();
  utils.clearAndPersistUserData(userAndIdDetails, userId, activeProject);
  window.KEYCLOAK_CONFIG.logout({
    redirectUri: `${window.location.origin}/project/overview`,
  });
};

export const initializeAxiosForKeycloak = () => {
  const showLoaderEvent = new Event('showLoader', { bubbles: true });
  const hideLoaderEvent = new Event('hideLoader', { bubbles: true });
  let numberOfApiCallsPending = 0;
  let hideLoaderDebounce;

  /**
   * Updates the visibility of the loader based on the number of ongoing API calls.
   *
   * This function ensures that the loader is displayed whenever there are pending API calls
   * and adds a slight delay (400ms) before hiding the loader once all calls are resolved.
   * The delay helps avoid flickering effects and ensures a smooth user experience.
   *
   * @function
   * @description
   * - **Show Loader**: Immediately dispatches a `showLoaderEvent` when there are pending API calls.
   * - **Hide Loader**: Adds a 400ms delay before dispatching `hideLoaderEvent` after all API calls are resolved.
   *   This prevents flickering and ensures that the loader does not disappear prematurely in rapid API cycles.
   *
   * @example
   * // Call this function whenever an API call starts or finishes
   * updateLoaderVisibility();
   */
  const updateLoaderVisibility = () => {
    clearTimeout(hideLoaderDebounce);
    if (numberOfApiCallsPending > 0) {
      document.dispatchEvent(showLoaderEvent);
    } else {
      hideLoaderDebounce = setTimeout(() => {
        document.dispatchEvent(hideLoaderEvent);
      }, 400); // Add delay only for hiding
    }
  };
  const headersCommonOptions = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  window.addEventListener('offline', () => {
    window.offlineSnack = window.enqueueSnackbar(
      'You are working in offline mode.',
      {
        variant: 'info',
        preventDuplicate: true,
        persist: true,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        action: () => {},
      }
    );
  });

  window.addEventListener('online', () => {
    if (window.offlineSnack) {
      window.closeSnackbar(window.offlineSnack);
    }
  });

  axios.defaults.headers.common = headersCommonOptions;

  axios.interceptors.request.use((req) => {
    if (req.loader !== false) {
      numberOfApiCallsPending += 1;
      updateLoaderVisibility();
    }

    const accessToken =
      localStorage.getItem('access_token') || window?.KEYCLOAK_CONFIG?.token;
    if (accessToken && utils.hostNeedsToken(accessToken, req.url)) {
      req.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (utils.hostNeedsOrg(req.url)) {
      req.headers.org = `${localStorage.getItem('REALM_ID')}`;
    }

    return req;
  });

  axios.interceptors.response.use(
    (response) => {
      if (response?.config?.loader !== false) {
        numberOfApiCallsPending -= 1;
        updateLoaderVisibility();
      }
      return response;
    },
    async (error) => {
      // eslint-disable-next-line no-nested-ternary
      const errorMessage = error?.response?.data?.msg
        ? typeof error?.response?.data?.msg === 'string'
          ? error.response.data.msg
          : JSON.stringify(error.response.data.msg)
        : error.message;

      // handling errors that are skipped(CORS etc.)
      utils.handleGlobalError(error);

      if (error?.config?.loader !== false) {
        numberOfApiCallsPending -= 1;
        updateLoaderVisibility();
      }

      if (
        error.response?.status === UNAUTHORIZED_RESPONSE_CODE &&
        !error?.config?._retry
      ) {
        error.config._retry = true;

        try {
          if (window.KEYCLOAK_CONFIG.isTokenExpired()) {
            SwalToast({
              icon: 'info',
              title:
                'Please wait your token might have expired. We are trying to refresh the token and resend the request.',
            });
            await window.KEYCLOAK_CONFIG.updateToken(1);
            const newToken = window.KEYCLOAK_CONFIG.token;
            if (newToken) {
              localStorage.setItem('access_token', newToken);
              SwalToast({
                icon: 'success',
                title: 'Token refreshed successfully. Resending API requests.',
              });
              axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axios.request(error.config);
            }
            SwalToast({
              icon: 'error',
              title: 'Failed to refresh the token.',
            });
            logout();
            return Promise.reject(error);
          }
          return Promise.reject(error);
        } catch (tokenRefreshError) {
          SwalToast({
            icon: 'error',
            title: 'Failed to refresh the token.',
          });
          logout();
          return Promise.reject(error);
        }
      }

      // This condition for intercepting API errors for which we need to show yellow/info type toast message.
      if (error?.response?.headers?.['x-info'] === 'True') {
        SwalToast({
          icon: 'info',
          title: errorMessage,
        });
      }
      SwalToast({
        icon: 'error',
        title: errorMessage,
      });
      return Promise.reject(error);
    }
  );
};

export default initializeAxiosForKeycloak;
